'use client';

import React from 'react';
import { ElementProps } from '@/components/sections/builder/types';
import { SquareMousePointer } from 'lucide-react';
import { IProduct } from '@/types/product';
import { ProductDetailButtonElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Button } from '@/components/ui/button';
import { useCartProduct } from '@/stores/cart';
import { toast } from '@/components/ui/use-toast';
import { addToCart, buyNow } from '@/lib/actions/cart/action';
import { cn } from '@/lib/utils';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { queryClient } from '@/lib/query-client';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { useBuilderStore } from '@/stores/builder';
import { DEFAULT_THEME_PRODUCT_DETAIL_BUTTON } from '@/components/sections/builder/theme/product-card';
import { CartPopup } from '@/components/sections/builder/elements/header/header/cart-dropdown';
import useModal from '@/hooks/useModal';
import { BaseCart } from '@/types/http/cart/common';
import { useProductStore } from '@/stores/product';
import Cookies from 'js-cookie';
import { Switch } from '@/components/ui/switch';
import { shallow } from 'zustand/shallow';

const defaultData: ProductDetailButtonElementDataType = {
  name: 'Add to cart Button',
  showButtonBuyNow: true,
  reverseButton: false,
  buttonLabelAddToCart: 'Add to cart',
  buttonLabelBuyNow: 'Buy now',
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const customerToken = Cookies.get('accessToken');
  const popupDrawer = useModal();

  const [isLoadingCart, setLoadingCart] = React.useState(false);
  const [isLoadingBuyNow, setLoadingBuyNow] = React.useState(false);
  const [cartPayload, setCartPayload] = React.useState<BaseCart | null>(null);

  const product = (element.parentData || {}) as IProduct;
  const productDetailButton = element.theme?.productDetailButton;

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const cartProduct = useCartProduct(
    (state) => ({
      item_id: state.item_id,
      variants: state.variants,
      sell_price: state.sell_price,
      discount_price: state.discount_price,
      sku: state.sku,
      item_weight: state.item_weight,
      item_height: state.item_height,
      item_width: state.item_width,
      item_length: state.item_length,
      quantity: state.quantity,
      setQuantity: state.setQuantity,
    }),
    shallow
  );
  const productStore = useProductStore(
    (state) => ({
      quantity: state.quantity,
      allSelectedVariants: state.allSelectedVariants,
      setQuantity: state.setQuantity,
    }),
    shallow
  );

  const themeContent = preview ? builder.theme : element.theme;

  const productVariants = product.variants;
  const quantity = productStore.quantity;
  const isSoldOut = (quantity as number) <= 0;

  const processCartAction = async (actionType: 'buyNow' | 'addToCart') => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      actionType === 'buyNow' ? setLoadingBuyNow(true) : setLoadingCart(true);

      if (isSoldOut) {
        toast({
          title: 'Error',
          description: 'Product is out of stock',
          variant: 'destructive',
        });
        return;
      }

      const selectedItem = productVariants ? cartProduct : product?.product_items?.[0];

      if (!selectedItem) {
        toast({
          title: 'Error',
          description: 'No product item available',
          variant: 'destructive',
        });
        return;
      }

      const {
        item_id: id,
        sell_price,
        discount_price,
        item_weight,
        item_height,
        item_width,
        item_length,
      } = selectedItem;

      const item = {
        id: id || '',
        product_name: product?.product_name,
        sell_price: Number(sell_price),
        discount_price: Number(discount_price),
        product_assets: product?.product_assets,
        quantity: cartProduct.quantity,
        variants: cartProduct.variants,
        sku: cartProduct.sku,
        item_weight: Number(item_weight),
        item_height: Number(item_height),
        item_width: Number(item_width),
        item_length: Number(item_length),
        total_value: Number(sell_price) * cartProduct.quantity,
      };

      const payload = {
        id: id || '',
        quantity: item.quantity,
      };

      if (actionType === 'addToCart') {
        await addToCart(host, payload, customerToken);
        setCartPayload(item as BaseCart);
        setTimeout(() => popupDrawer.open(), 1000);
        cartProduct.setQuantity(cartProduct.quantity > 0 ? 1 : 0);
        queryClient.invalidateQueries({ queryKey: ['carts'] });
      } else {
        const result = await buyNow(host, payload, customerToken);
        if (result?.success) {
          window.location.href = `/checkout/${result.data?.checkout_id}`;
        }
      }

      toast({
        duration: 1000,
        title: 'Success',
        description:
          actionType === 'addToCart' ? 'Product has been added to cart' : 'Proceeding to checkout',
        variant: 'success',
      });

      SendEventAnalytic('add_to_cart', {
        currency: 'IDR',
        value: Number(sell_price) * cartProduct.quantity,
        items: [
          {
            item_name: item.product_name,
            item_id: item.id,
            price: Number(item.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });

      SendEventMetapixel('AddToCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_category: product?.category_name,
        content_type: 'product',
        quantity: item.quantity,
        value: Number(sell_price) * cartProduct.quantity,
        currency: 'IDR',
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        `failed to ${actionType === 'addToCart' ? 'add product to cart' : 'process Buy Now'}`,
        error
      );
      toast({
        title: 'Error',
        description: `Failed to ${
          actionType === 'addToCart' ? 'add product to cart' : 'process Buy Now'
        }`,
        variant: 'destructive',
      });
    } finally {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      actionType === 'buyNow' ? setLoadingBuyNow(false) : setLoadingCart(false);
    }
  };

  const handleAddToCart = () => processCartAction('addToCart');
  const handleBuyNow = () => processCartAction('buyNow');

  const disableButton =
    isSoldOut ||
    isLoadingCart ||
    isLoadingBuyNow ||
    quantity === 0 ||
    !!(productVariants && !productStore.allSelectedVariants);

  return (
    <div
      className={cn(
        'w-full gap-3',
        productDetailButton?.reverseButton ? 'flex-col-reverse' : 'flex-col',
        themeContent?.productCard?.positionButtonAddToCart === 'fixed' ? 'hidden' : 'flex'
      )}
    >
      {productDetailButton?.showButtonBuyNow && (
        <Button
          className={cn(
            'flex w-full items-center px-4',
            'theme_button',
            'theme_button_modal_product_detail'
          )}
          disabled={disableButton}
          onClick={handleBuyNow}
          data-testid='product-detail-button-buy-now'
        >
          {isLoadingBuyNow ? (
            <Spinner size='small' className='mr-2' />
          ) : (
            productDetailButton?.buttonLabelBuyNow || defaultData.buttonLabelBuyNow
          )}
        </Button>
      )}
      <Button
        className={cn(
          'flex  w-full items-center !border !border-primary px-4',
          'theme_button',
          'theme_button_modal_product_detail'
        )}
        variant='outline'
        onClick={handleAddToCart}
        disabled={disableButton}
        isLoading={isLoadingCart}
        data-testid='product-detail-button-add-to-cart'
      >
        {isLoadingCart ? (
          <Spinner size='small' className='mr-2' />
        ) : (
          productDetailButton?.buttonLabelAddToCart || defaultData.buttonLabelAddToCart
        )}
      </Button>
      {element?.theme?.cart?.cartType === 'popup_notification' && popupDrawer.isShow ? (
        <CartPopup
          openModal={popupDrawer.isShow}
          cart={cartPayload}
          setOpenModal={popupDrawer.setIsShow}
          host={window.location.host}
        />
      ) : null}
    </div>
  );
};

export const ElementPreviewIcon = SquareMousePointer;

export function ElementSetting() {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );
  const productDetailButton =
    builder.theme.productDetailButton || DEFAULT_THEME_PRODUCT_DETAIL_BUTTON;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Button Settings</h1>
      <div className='mb-4 flex items-center gap-2'>
        <label>Show button buy now</label>
        <Switch
          className='ml-auto'
          checked={productDetailButton.showButtonBuyNow}
          onCheckedChange={(checked) => {
            builder.setTheme({
              productDetailButton: {
                ...productDetailButton,
                showButtonBuyNow: !!checked,
              },
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label>Reverse button</label>
        <Switch
          className='ml-auto'
          checked={productDetailButton.reverseButton}
          onCheckedChange={(checked) => {
            builder.setTheme({
              productDetailButton: {
                ...productDetailButton,
                reverseButton: !!checked,
              },
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label add to cart</label>
        <Input
          name='buttonLabelAddToCart'
          defaultValue={productDetailButton.buttonLabelAddToCart}
          value={productDetailButton.buttonLabelAddToCart}
          maxLength={30}
          className='w-full p-2 text-xs'
          onChange={(e) => {
            builder.setTheme({
              productDetailButton: {
                ...productDetailButton,
                buttonLabelAddToCart: e.target.value,
              },
            });
          }}
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label buy now</label>
        <Input
          name='buttonLabelBuyNow'
          className='w-full p-2 text-xs'
          defaultValue={productDetailButton.buttonLabelBuyNow}
          value={productDetailButton.buttonLabelBuyNow}
          maxLength={30}
          onChange={(e) => {
            builder.setTheme({
              productDetailButton: {
                ...productDetailButton,
                buttonLabelBuyNow: e.target.value,
              },
            });
          }}
        />
      </div>
    </div>
  );
}

const ProductDetailButton = {
  type: 'button_cart',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailButton;
